import { m, type Message } from '@/i18n';
import type { ConditionFactKind, Fact, FactKind } from './facts';
import {
  CONDITION_FACT_KINDS,
  factAgeDays,
  FACTS_NEEDING_A_NUMBER,
  isConditionFact,
} from './facts';
import { foldTag, parseTagPath } from './item-tags';
import { laneValueOf, parseLaneAttribute, type LaneAttribute } from './lane-attributes';
import type { LaneAttributeSnapshot } from './lane-attributes';
import { errOf, isRecord, ok, type Result } from './types';

/**
 * Fact RULES, the one shape in two jurisdictions (docs/THRESHOLDS.md §1/§2).
 *
 * A fact is authorless arithmetic ("last act 9 days ago"). A RULE is the
 * judgment on top of it: "at 14 days, call it *Stalled*, at warning". The
 * threshold and the word are the judgment, so they need an author, and the
 * author is either
 *
 *   - a **lens** (`BoardFactRule`, docs/HEALTH_SIGNALS.md §3): the reading
 *     never leaves the board and never touches the item (HS-D1), so it needs
 *     no word - the board colours what it colours; or
 *   - a **flow** (`FlowFactRule`, THRESHOLDS §2): a process the item consented
 *     to join, which is what lets its rules reach item truth. It carries the
 *     WORD as well, because a flow's vocabulary is what the item then speaks.
 *
 * What the platform keeps is what a grade COSTS (`domain/channels.ts`: critical
 * pages, warning takes the dot, notice never pushes). An author may grade
 * anything critical; nobody may make critical quiet.
 *
 * Everything here is pure: the facts and the item snapshot arrive as inputs.
 */

/**
 * The grades a rule may declare (docs/HEALTH_SIGNALS.md §6): the three severity
 * channels. `info` is deliberately absent - a rule that means nothing louder
 * than the register is no rule; the fact already shows there.
 */
export const FACT_RULE_GRADES = ['notice', 'warning', 'critical'] as const;
export type FactRuleGrade = (typeof FACT_RULE_GRADES)[number];

/**
 * The human word each fact kind wears in a rule's PROVENANCE line ("per this
 * board's rule: late = warning"). DISPLAY ONLY - a rule is keyed on the stable
 * `FactKind`, never on this word or any other label string (HS-A6, the chain of
 * authorship: reading a NAME and guessing meaning is the banned unauthored
 * link). `Record<FactKind, _>` keeps the map exhaustive by type: a new fact kind
 * fails compilation here until it gets a word.
 */
export const FACT_RULE_WORD: Record<FactKind, Message> = {
  quiet: m('facts.word.quiet'),
  end_passed: m('facts.word.endPassed'),
  start_passed: m('facts.word.startPassed'),
  no_requester: m('facts.word.noRequester'),
  needs_doing: m('facts.word.needsDoing'),
  dependents: m('facts.word.dependents'),
  back_pressure: m('facts.word.backPressure'),
  committed_but_inert: m('facts.word.committedButInert'),
  running_on_old_intent: m('facts.word.runningOnOldIntent'),
  unattended_risk: m('facts.word.unattendedRisk'),
  schedule_conflict: m('facts.word.scheduleConflict'),
  tagged: m('facts.word.tagged'),
  in_stage: m('facts.word.inStage'),
};

/** The closed set of fact kinds a rule of EITHER jurisdiction may name,
 *  derived from the exhaustive word map: every fact but the two conditions,
 *  which only a flow's rule names (docs/THRESHOLDS.md §2, "Conditions"). */
export const RULE_FACT_KINDS = (Object.keys(FACT_RULE_WORD) as FactKind[]).filter(
  (kind) => !CONDITION_FACT_KINDS.has(kind),
);

/** The conditions a FLOW's rule may name beside the facts (2026-10-14): the
 *  member carries a tag, or stands in one of the flow's stored stages. */
export const CONDITION_RULE_KINDS: readonly ConditionFactKind[] = ['tagged', 'in_stage'];

/**
 * The rule core both jurisdictions share: which fact, at which grade, past how
 * many days, optionally only while the item wears some attribute value.
 *
 * `thresholdDays` is what THRESHOLDS.md is about. It is **required** for the
 * facts that measure a silence nobody put a number on (`FACTS_NEEDING_A_NUMBER`)
 * and optional elsewhere: "late" is already late by the author's own date, and a
 * threshold there ("late by more than 3 days") only refines it.
 */
export interface FactRule {
  /** The stable fact kind the rule fires on (domain/facts.ts). */
  fact: FactKind;
  /** The severity grade this jurisdiction reads the fact at. */
  grade: FactRuleGrade;
  /** Days the fact's own measure must reach before the rule fires. Absent = no
   *  threshold (legal only where the fact carries a human's number already). */
  thresholdDays?: number;
  /** Optional condition: the rule fires only while the item occupies this
   *  attribute VALUE (the laneable-attribute canon, domain/lane-attributes.ts -
   *  e.g. `while Active`; the retired standing tri-state never comes back). */
  when?: { attr: LaneAttribute; value: string };
  /** A `tagged` rule's tag, as its author spelled it (compared folded). A rule
   *  on `x` holds for a member carrying `x` or any tag under it (`x/y`), the
   *  `tag:` criterion's descendant match (docs/THRESHOLDS.md §2). */
  tag?: string;
  /** An `in_stage` rule's stage: the key of a stored stage of the rule's flow. */
  stage?: string;
}

/**
 * What a fired rule DOES (Leon, 2026-07-27, the second turn):
 *
 *   - `declare` - it applies. "At 14 days this IS Stalled, at warning" is a
 *     declaration by an author the item consented to when it joined the flow
 *     (HEALTH_SIGNALS §3's flow-contracted jurisdiction), so it lands on the
 *     item like a stage's Then-block declaration: item truth, visible to
 *     everyone who can see the ITEM, no acceptance step. It clears the way
 *     every machine statement clears - the world changes, or the rule does.
 *   - `propose` - it asks. A conditional SUGGESTION: the flow's word offered
 *     at the grade it would take on, colouring nothing until a human accepts,
 *     and accepting declares that word verbatim in the accepter's name.
 *
 * The visibility question only bites the second kind (Leon: "only a rule that
 * creates a conditional suggestion runs into the who-can-see/answer it"): an
 * UNAPPLIED suggestion is invisible to anyone who cannot see the rule, because
 * they could neither answer it nor inspect it. An applied declaration is on the
 * item, so it is everybody's.
 */
export const FACT_RULE_MODES = ['declare', 'propose'] as const;
export type FactRuleMode = (typeof FACT_RULE_MODES)[number];

/**
 * The mode a rule takes when its author has not said (Leon, 2026-07-27: *"late
 * is a fact, stalled a threshold that should propose"*).
 *
 * The distinction is whether the rule adds a JUDGMENT:
 *
 *   - a rule with no threshold only NAMES something already authored - "the
 *     declared end passed" is arithmetic against a date its author chose, and
 *     calling that *Late* introduces nothing to argue with. It may declare;
 *   - a rule that carries a threshold IS the judgment (§0: "we can default and
 *     assume, but really we need to ask because we cannot properly know"). Its
 *     number is exactly the thing nobody can know for someone else, so the safe
 *     default is to ask rather than to paint.
 *
 * A default, never a law: an author may set either mode on either kind. What it
 * decides is which way a rule leans when nobody stated it - and it leans toward
 * asking exactly where this paper says knowledge runs out.
 */
export function defaultRuleMode(rule: Pick<FactRule, 'fact' | 'thresholdDays'>): FactRuleMode {
  return rule.thresholdDays !== undefined || FACTS_NEEDING_A_NUMBER.has(rule.fact)
    ? 'propose'
    : 'declare';
}

/** A flow's rule: the core, the WORD the flow calls the fact by, and what
 *  firing DOES. The word is free text on purpose (Leon, 2026-07-26: "fully
 *  custom is more true to the nature of the product") - our own vocabulary
 *  ships as the seeded flow's DATA, a catalogue to copy rather than one to
 *  choose from. */
export interface FlowFactRule extends FactRule {
  word: string;
  mode: FactRuleMode;
}

/**
 * Does a rule fire on one fact? The whole predicate, shared by both
 * jurisdictions so a lens and a flow can never disagree about what "past 14
 * days" means.
 *
 * The threshold reads the fact's OWN measure (`factAgeDays`): quiet days,
 * lateness, the intention/attention clock. A fact needing a number and a rule
 * carrying none does **not** fire - the safe direction, and the one the
 * unconditional flip demands: `quiet` is true from day one now, so a pre-flip
 * lens rule with no threshold would otherwise colour every card the moment it
 * was created (docs/THRESHOLDS.md §5a).
 */
export function factRuleFires(
  fact: Fact,
  rule: FactRule,
  item: LaneAttributeSnapshot,
  /** The rule's flow. A condition fact is about one flow's member, and only
   *  that flow's rule speaks about it; a lens rule (no flow) never does. */
  flowId?: string,
): boolean {
  if (fact.kind !== rule.fact) return false;
  if (isConditionFact(fact)) {
    if (flowId === undefined || fact.flowId !== flowId) return false;
    if (fact.kind === 'tagged' && (rule.tag === undefined || fact.tag !== foldTag(rule.tag))) {
      return false;
    }
    if (fact.kind === 'in_stage' && fact.stage !== rule.stage) return false;
  }
  if (rule.when !== undefined && laneValueOf(rule.when.attr, item) !== rule.when.value) {
    return false;
  }
  if (rule.thresholdDays === undefined) return !FACTS_NEEDING_A_NUMBER.has(fact.kind);
  const age = factAgeDays(fact);
  return age !== null && age >= rule.thresholdDays;
}

/**
 * The fact a rule speaks about among one item's facts, or undefined when it
 * holds for none: the first it fires on. A condition rule matches at most one
 * fact (`conditionFactsFor` computes one per rule and member), so this is the
 * instance its crossing cites and its ask attaches to.
 */
export function firingFact(
  facts: readonly Fact[],
  rule: FactRule,
  item: LaneAttributeSnapshot,
  flowId?: string,
): Fact | undefined {
  return facts.find((f) => factRuleFires(f, rule, item, flowId));
}

/**
 * Which statement a rule speaks about, without any instance value: the fact
 * kind, or for a condition the kind, the flow and the parameter. Keys a
 * proposal to the statement it offers a word for (two tag rules of one flow
 * are two statements). Carries nothing an anchor carries.
 */
export function factSpeechKey(fact: Fact): string {
  if (fact.kind === 'tagged') return `tagged:${fact.flowId}:${fact.tag}`;
  if (fact.kind === 'in_stage') return `in_stage:${fact.flowId}:${fact.stage}`;
  return fact.kind;
}

/** The rule's anchor on the flow page (`#rule-<this>`, THRESHOLDS §2's door):
 *  the fact kind, or for a condition the kind and its parameter. */
export function ruleAnchor(rule: FactRule): string {
  if (rule.fact === 'tagged') return `tagged-${foldTag(rule.tag ?? '')}`;
  if (rule.fact === 'in_stage') return `in_stage-${rule.stage ?? ''}`;
  return rule.fact;
}

/** The identity two rules of one flow may not share: one word per statement
 *  (a fact kind, or a condition with its parameter). */
export function ruleIdentity(rule: FactRule): string {
  return ruleAnchor(rule);
}

/** The rule's own line, for a provenance chain: "quiet past 14 days", "carries
 *  #legal", "in Review". `stageLabel` is the flow's word for an `in_stage`
 *  rule's stage, where the caller knows it (else the key). */
export function factRuleCondition(rule: FactRule, stageLabel?: string): Message {
  if (rule.fact === 'tagged') return m('facts.condition.tagged', { tag: rule.tag ?? '' });
  if (rule.fact === 'in_stage') {
    return m('facts.condition.inStage', { stage: stageLabel ?? rule.stage ?? '' });
  }
  const fact = FACT_RULE_WORD[rule.fact];
  return rule.thresholdDays === undefined
    ? fact
    : m('facts.condition.pastDays', { fact, count: rule.thresholdDays });
}

const GRADE_RANK: Record<FactRuleGrade, number> = { notice: 0, warning: 1, critical: 2 };

/** Worst-first, the collector's order (the fold shows the worst, the register
 *  enumerates). Shared so every consumer sorts the same way. */
export function byWorstGrade<T extends { grade: FactRuleGrade }>(a: T, b: T): number {
  return GRADE_RANK[b.grade] - GRADE_RANK[a.grade];
}

// --- Parsing (shape only; the world checks live with each jurisdiction) -------

const asGrade = (value: unknown): FactRuleGrade | null =>
  typeof value === 'string' && (FACT_RULE_GRADES as readonly string[]).includes(value)
    ? (value as FactRuleGrade)
    : null;

const asFactKind = (value: unknown): FactKind | null =>
  typeof value === 'string' && (RULE_FACT_KINDS as readonly string[]).includes(value)
    ? (value as FactKind)
    : null;

/**
 * Parse one rule core. `where` names the jurisdiction in the error text, since
 * the same message reaches a board's settings and a flow's rules editor.
 *
 * A threshold is a positive whole number of days: fractional or negative days
 * are not a judgment anyone means, and the facts count whole days.
 */
export function parseFactRuleCore(value: unknown, where: Message): Result<FactRule> {
  if (!isRecord(value))
    return errOf('invalid_input', m('flowErrors.factRule.notObject', { where }));
  const fact = asFactKind(value.fact);
  if (fact === null) return errOf('invalid_input', m('flowErrors.factRule.unknownFact', { where }));
  const grade = asGrade(value.grade);
  if (grade === null)
    return errOf('invalid_input', m('flowErrors.factRule.unknownGrade', { where }));
  const rule: FactRule = { fact, grade };
  if (value.thresholdDays !== undefined && value.thresholdDays !== null) {
    const days = value.thresholdDays;
    if (typeof days !== 'number' || !Number.isInteger(days) || days < 0) {
      return errOf('invalid_input', m('flowErrors.factRule.wholeDays', { where }));
    }
    rule.thresholdDays = days;
  }
  if (isRecord(value.when)) {
    const attr = parseLaneAttribute(value.when.attr);
    if (attr !== null && typeof value.when.value === 'string' && value.when.value.length > 0) {
      rule.when = { attr, value: value.when.value };
    }
  }
  return ok(rule);
}

/**
 * The rule of THRESHOLDS.md §0, enforced at save: a fact that measures a silence
 * nobody numbered may not be graded without a threshold. Refusing here is what
 * keeps "we can default and assume, but really we need to ask" honest - the
 * editor cannot save a judgment with the number left out.
 */
export function factRuleNeedsThreshold(rule: FactRule): boolean {
  return FACTS_NEEDING_A_NUMBER.has(rule.fact) && rule.thresholdDays === undefined;
}

// --- The flow's side: signals, not tags --------------------------------------

/**
 * A flow rule that FIRED on one item (docs/THRESHOLDS.md §2).
 *
 * Deliberately not a `tags` catalog row: the catalog is org-scoped, and minting
 * a flow's word there would hand the word - and its severity - back to a
 * jurisdiction the item never opted into. So this is computed at read time from
 * (fact, rule) and stored nowhere.
 *
 * `mode` decides what it then IS. A `declare` firing is **item truth by
 * consent** - the item joined the flow, the same corollary that lets a stage
 * word govern a member - and it carries no declarer and no clear control,
 * because nobody clicked: it ends when the fact ends or the rule changes. A
 * `propose` firing is an ask, and colours nothing until someone accepts.
 * (A lens signal, by contrast, never leaves its board - HS-D1.)
 */
export interface FlowSignal {
  /** What this firing DOES: apply on the item, or ask its people. */
  mode: FactRuleMode;
  /** The authoring flow (the definition row id) - also the door's target. */
  flowId: string;
  /** The flow's display name, for the provenance line. */
  flowName: string;
  /** Which rule of that flow fired: the affordance opens the rules editor AT
   *  this rule (THRESHOLDS §4 - a door to the decision, never a name to
   *  resent). */
  ruleIndex: number;
  /** The rule's anchor on the flow page (`ruleAnchor`). */
  anchor: string;
  fact: FactKind;
  /** The statement it speaks about (`factSpeechKey` of the fact it fired on):
   *  a condition rule's ask attaches to its own statement, not to every
   *  statement of the kind. */
  speechKey: string;
  grade: FactRuleGrade;
  /** The flow's own word for this fact. Display only; machinery counts grades. */
  word: string;
  /** The rule's number, when it carried one - the crossing event names the
   *  boundary it crossed ("quiet past 14 days"), and a boundary with no number
   *  is the author's own declared value. */
  thresholdDays?: number;
  /** Whether THIS viewer can open the rule (docs/THRESHOLDS.md §4). An applied
   *  word is item truth, so it reaches people with no reach on the flow that
   *  authored it - they get the chain ("per Delivery: quiet past 14 days") and
   *  no door, rather than a link that 404s. The read sets it; the domain never
   *  guesses at reach. */
  reachable: boolean;
  /** The one-line chain: "per Lifecycle: quiet past 14 days". */
  provenance: Message;
}

/**
 * Evaluate a flow's rules against one member's facts. Pure: the read gathers
 * the memberships, the definitions and the facts, and calls this per (item,
 * flow). Worst grade first, the collector's order.
 */
export function evaluateFlowFactRules(
  facts: readonly Fact[],
  rules: readonly FlowFactRule[],
  item: LaneAttributeSnapshot,
  flow: { id: string; name: string },
): FlowSignal[] {
  if (rules.length === 0 || facts.length === 0) return [];
  const fired: FlowSignal[] = [];
  rules.forEach((rule, ruleIndex) => {
    const fact = firingFact(facts, rule, item, flow.id);
    if (fact === undefined) return;
    fired.push({
      mode: rule.mode,
      // The read overwrites this per viewer; the pure evaluator has no reach to
      // consult, and false is the honest default (no door until someone says).
      reachable: false,
      flowId: flow.id,
      flowName: flow.name,
      ruleIndex,
      anchor: ruleAnchor(rule),
      fact: rule.fact,
      speechKey: factSpeechKey(fact),
      grade: rule.grade,
      word: rule.word,
      ...(rule.thresholdDays !== undefined ? { thresholdDays: rule.thresholdDays } : {}),
      provenance: m('facts.provenance.flowRule', {
        flow: flow.name,
        condition: factRuleCondition(rule, fact.kind === 'in_stage' ? fact.stageLabel : undefined),
      }),
    });
  });
  return fired.sort(byWorstGrade);
}

/**
 * Parse one flow rule: the shared core plus the flow's WORD. The word is free
 * text on purpose (THRESHOLDS §1) and simply must exist - a grade with no word
 * would put an unnamed colour on the item, which is the unauthored link the
 * chain-of-authorship rule bans (HS-A6).
 */
export function parseFlowFactRule(value: unknown, where: Message): Result<FlowFactRule> {
  if (isRecord(value) && (CONDITION_RULE_KINDS as readonly unknown[]).includes(value.fact)) {
    return parseConditionRule(value, where);
  }
  const core = parseFactRuleCore(value, where);
  if (!core.ok) return core;
  const raw = value as Record<string, unknown>;
  const word = typeof raw.word === 'string' ? raw.word.trim() : '';
  if (word.length === 0)
    return errOf('invalid_input', m('flowErrors.factRule.needsWord', { where }));
  // An unstated mode follows `defaultRuleMode`: a threshold-bearing rule asks,
  // a rule that merely names an authored fact states. An unrecognized value
  // falls back the same way rather than refusing the rule.
  const mode: FactRuleMode =
    raw.mode === 'propose' || raw.mode === 'declare'
      ? (raw.mode as FactRuleMode)
      : defaultRuleMode(core.value);
  if (factRuleNeedsThreshold(core.value)) {
    return errOf(
      'invalid_input',
      m('flowErrors.factRule.needsThreshold', { where, fact: FACT_RULE_WORD[core.value.fact] }),
    );
  }
  return ok({ ...core.value, word, mode });
}

/**
 * Parse a CONDITION rule (docs/THRESHOLDS.md §2, "Conditions", 2026-10-14): a
 * flow's word for a member that carries a tag or stands in a stage. Same parts
 * as a fact rule - word, grade, mode, an optional `when` - and a parameter
 * instead of a threshold: the tag (a valid tag path, kept as spelled) or the
 * stage key (that it names a stored stage of the flow is the definition's
 * check, `validateFlowDefinition`). A threshold is refused: "in a stage for
 * more than N days" is not built, and a number here would be silently ignored.
 * The mode defaults to declare (`defaultRuleMode`: no threshold).
 */
function parseConditionRule(value: Record<string, unknown>, where: Message): Result<FlowFactRule> {
  const fact = value.fact as ConditionFactKind;
  const grade = asGrade(value.grade);
  if (grade === null)
    return errOf('invalid_input', m('flowErrors.factRule.unknownGrade', { where }));
  if (value.thresholdDays !== undefined && value.thresholdDays !== null) {
    return errOf('invalid_input', m('flowErrors.factRule.conditionNoDays', { where }));
  }
  const rule: FactRule = { fact, grade };
  if (fact === 'tagged') {
    const parsed = typeof value.tag === 'string' ? parseTagPath(value.tag) : null;
    if (parsed === null || !parsed.ok) {
      return errOf('invalid_input', m('flowErrors.factRule.needsTag', { where }));
    }
    rule.tag = parsed.value.tag;
  } else {
    const stage = typeof value.stage === 'string' ? value.stage.trim() : '';
    if (stage.length === 0) {
      return errOf('invalid_input', m('flowErrors.factRule.needsStage', { where }));
    }
    rule.stage = stage;
  }
  if (isRecord(value.when)) {
    const attr = parseLaneAttribute(value.when.attr);
    if (attr !== null && typeof value.when.value === 'string' && value.when.value.length > 0) {
      rule.when = { attr, value: value.when.value };
    }
  }
  const word = typeof value.word === 'string' ? value.word.trim() : '';
  if (word.length === 0)
    return errOf('invalid_input', m('flowErrors.factRule.needsWord', { where }));
  const mode: FactRuleMode =
    value.mode === 'propose' || value.mode === 'declare'
      ? (value.mode as FactRuleMode)
      : defaultRuleMode(rule);
  return ok({ ...rule, word, mode });
}
